import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Pencil, Eye, EyeOff } from "lucide-react";
import api, { fmtErr } from "../../lib/api";

// Interruptor pill reutilizável
const Toggle = ({ on, onClick, labelOn = "Sim", labelOff = "Não", testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    role="switch"
    aria-checked={on}
    className="inline-flex items-center gap-2 group"
    title={on ? labelOn : labelOff}
  >
    <span
      className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${on ? "bg-green-500" : "bg-black/20"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${on ? "translate-x-4" : ""}`}
      />
    </span>
    <span className={`text-xs font-light ${on ? "text-green-700" : "text-black/40"}`}>
      {on ? labelOn : labelOff}
    </span>
  </button>
);

export const ServicesAdmin = () => {
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [form, setForm] = useState({ name: "", category: "", duration_min: "60", price: "" });
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/booking-services").then((res) => setServices(res.data)).finally(() => setLoading(false));
  const loadStaff = () =>
    api.get("/admin/professionals").then((res) => setProfessionals(res.data.map((p) => p.name))).catch(() => {});

  useEffect(() => {
    load();
    loadStaff();
  }, []);

  const categories = [...new Set(services.map((s) => s.category).filter(Boolean))];

  // Alternar campo booleano inline (optimista) via PATCH
  const patchField = async (id, field, value) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
    try {
      await api.patch(`/admin/booking-services/${id}`, { [field]: value });
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
      load(); // reverter em caso de erro
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/admin/booking-services", {
        name: form.name.trim(),
        category: form.category.trim() || "Outros",
        duration_min: parseInt(form.duration_min, 10),
        price: form.price === "" ? null : parseFloat(form.price),
        active: true,
        bookable: true,
        show_price: true,
        staff: [],
      });
      toast.success("Serviço adicionado.");
      setForm({ name: "", category: "", duration_min: "60", price: "" });
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este serviço das marcações?")) return;
    await api.delete(`/admin/booking-services/${id}`);
    toast.success("Serviço removido.");
    load();
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setDraft({
      ...s,
      category: s.category || "Outros",
      staff: s.staff || [],
      active: s.active !== false,
      bookable: s.bookable !== false,
      show_price: s.show_price !== false,
    });
  };

  const toggleStaff = (name) =>
    setDraft((d) => ({
      ...d,
      staff: d.staff.includes(name) ? d.staff.filter((n) => n !== name) : [...d.staff, name],
    }));

  const saveEdit = async () => {
    try {
      await api.put(`/admin/booking-services/${editId}`, {
        name: draft.name.trim(),
        category: (draft.category || "Outros").trim(),
        duration_min: parseInt(draft.duration_min, 10),
        price: draft.price === "" || draft.price == null ? null : parseFloat(draft.price),
        active: draft.active,
        bookable: draft.bookable,
        show_price: draft.show_price,
        staff: draft.staff,
      });
      toast.success("Serviço atualizado.");
      setEditId(null);
      setDraft(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const inputCls =
    "bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black";

  return (
    <div data-testid="services-admin" className="space-y-6">
      <div className="bg-white border border-black/5 p-6">
        <p className="text-sm text-black/40 font-light mb-6">
          Serviços disponíveis no site para agendamento. Ative/desative o <strong>preço</strong> e se está{" "}
          <strong>online</strong> diretamente na lista, sem abrir o editor. Use o lápis para categoria,
          duração, profissionais e mais. A duração define os horários livres.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-light min-w-[820px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-black/40 border-b border-black/10">
                <th className="py-2 pr-2">Serviço</th>
                <th className="py-2 pr-2">Categoria</th>
                <th className="py-2 pr-2 text-right">Duração</th>
                <th className="py-2 pr-2 text-right">Preço</th>
                <th className="py-2 pr-2 text-center">Preço visível</th>
                <th className="py-2 pr-2">Profissionais</th>
                <th className="py-2 pr-2 text-center">Online</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="py-6 text-black/30">A carregar…</td></tr>
              )}
              {!loading && services.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-black/30">Sem serviços. Adicione o primeiro em baixo.</td></tr>
              )}
              {!loading && services.map((s) => {
                const online = s.bookable !== false && s.active !== false;
                const priceVisible = s.show_price !== false;
                return (
                  <tr
                    key={s.id}
                    data-testid={`svc-row-${s.id}`}
                    className={`border-b border-black/5 last:border-0 ${s.active === false ? "opacity-50" : ""}`}
                  >
                    <td className="py-3 pr-2 font-medium">{s.name}</td>
                    <td className="py-3 pr-2 text-black/60">{s.category || "—"}</td>
                    <td className="py-3 pr-2 text-right whitespace-nowrap">{s.duration_min} min</td>
                    <td className="py-3 pr-2 text-right whitespace-nowrap">
                      <span className={priceVisible ? "" : "text-black/30 line-through"}>
                        {s.price == null ? "sob consulta" : `${Number(s.price).toFixed(2)} €`}
                      </span>
                    </td>
                    <td className="py-3 pr-2 text-center">
                      <button
                        type="button"
                        data-testid={`svc-toggle-price-${s.id}`}
                        onClick={() => patchField(s.id, "show_price", !priceVisible)}
                        className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors ${
                          priceVisible
                            ? "border-green-200 bg-green-50 text-green-700"
                            : "border-black/15 text-black/40 hover:border-black/40"
                        }`}
                        title={priceVisible ? "Preço visível — clique para ocultar" : "Preço oculto — clique para mostrar"}
                      >
                        {priceVisible ? <Eye size={13} /> : <EyeOff size={13} />}
                        {priceVisible ? "Visível" : "Oculto"}
                      </button>
                    </td>
                    <td className="py-3 pr-2 text-black/50 text-xs">{(s.staff && s.staff.length) ? s.staff.join(", ") : "Todos"}</td>
                    <td className="py-3 pr-2 text-center">
                      <div className="inline-flex justify-center">
                        <Toggle
                          on={online}
                          onClick={() => patchField(s.id, "bookable", !(s.bookable !== false))}
                          labelOn="Online"
                          labelOff="Offline"
                          testid={`svc-toggle-online-${s.id}`}
                        />
                      </div>
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`svc-edit-${s.id}`}
                        onClick={() => startEdit(s)}
                        className="p-1.5 hover:bg-black/5 text-black/60"
                        aria-label="Editar"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`svc-delete-${s.id}`}
                        onClick={() => remove(s.id)}
                        className="p-1.5 hover:bg-black/5 text-red-600"
                        aria-label="Remover"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Formulário rápido para adicionar */}
      <div className="bg-white border border-black/5 p-6">
        <p className="label-editorial text-black/50 mb-4">Adicionar serviço</p>
        <form onSubmit={add} className="flex flex-wrap gap-2 items-end">
          <input
            data-testid="svc-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome do serviço"
            className={`${inputCls} flex-1 min-w-[160px]`}
          />
          <input
            data-testid="svc-category-input"
            list="svc-cats-new"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="Categoria"
            className={`${inputCls} w-40`}
          />
          <datalist id="svc-cats-new">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          <input
            data-testid="svc-duration-input"
            type="number"
            min="15"
            step="15"
            value={form.duration_min}
            onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
            placeholder="Duração (min)"
            className={`${inputCls} w-32`}
          />
          <input
            data-testid="svc-price-input"
            type="number"
            min="0"
            step="0.5"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder="Preço €"
            className={`${inputCls} w-28`}
          />
          <button data-testid="svc-add-btn" type="submit" className="border border-black/20 px-4 py-2.5 hover:border-black transition-colors flex items-center gap-2" aria-label="Adicionar serviço">
            <Plus size={15} /> Adicionar
          </button>
        </form>
      </div>

      {/* Editor completo (modal) */}
      {editId && draft && (
        <div
          data-testid="svc-editor-overlay"
          className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto"
          onClick={() => { setEditId(null); setDraft(null); }}
        >
          <div
            data-testid="svc-editor"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-black/10 p-6 md:p-8 space-y-4 w-full max-w-2xl my-8 animate-pop-in shadow-xl"
          >
            <div className="flex items-center justify-between">
              <p className="label-editorial text-black/50">Editar serviço</p>
              <button type="button" onClick={() => { setEditId(null); setDraft(null); }} data-testid="svc-editor-close" className="text-black/40 hover:text-black">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-black/40 mb-1.5">Nome do serviço</label>
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Ex.: Blow Dry" className={`${inputCls} w-full`} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-black/40 mb-1.5">Categoria</label>
                <input list="svc-cats" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Ex.: Cabelo" className={`${inputCls} w-full`} />
                <datalist id="svc-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-black/40 mb-1.5">Duração (min)</label>
                  <input type="number" min="15" step="15" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: e.target.value })} placeholder="30" className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-black/40 mb-1.5">Preço (€)</label>
                  <input type="number" min="0" step="0.5" value={draft.price ?? ""} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="13" className={`${inputCls} w-full`} />
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-black/40 mb-2">Profissionais que executam (nenhum = todos)</p>
              <div className="flex flex-wrap gap-2">
                {professionals.map((name) => {
                  const on = draft.staff.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      data-testid={`svc-staff-${name}`}
                      onClick={() => toggleStaff(name)}
                      className={`text-sm px-3 py-1.5 border transition-colors ${on ? "bg-black text-white border-black" : "border-black/15 text-black/60 hover:border-black"}`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-1 border-t border-black/5">
              <label className="flex items-center gap-2 text-sm font-light cursor-pointer">
                <input type="checkbox" data-testid="svc-active" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Ativo
              </label>
              <label className="flex items-center gap-2 text-sm font-light cursor-pointer">
                <input type="checkbox" data-testid="svc-bookable" checked={draft.bookable} onChange={(e) => setDraft({ ...draft, bookable: e.target.checked })} /> Agendável online
              </label>
              <label className="flex items-center gap-2 text-sm font-light cursor-pointer">
                <input type="checkbox" data-testid="svc-show-price" checked={draft.show_price} onChange={(e) => setDraft({ ...draft, show_price: e.target.checked })} /> Mostrar preço
              </label>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <div className="flex-1" />
              <button type="button" onClick={() => { setEditId(null); setDraft(null); }} className="text-sm text-black/50 hover:text-black">Cancelar</button>
              <button data-testid="svc-save-btn" onClick={saveEdit} className="label-editorial bg-black text-white px-6 py-2.5 hover:bg-black/80 transition-colors">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesAdmin;
